import { useFetcher } from "@remix-run/react";
import {
  Elements,
  useStripe,
  useElements,
  ExpressCheckoutElement,
  PaymentElement,
} from "@stripe/react-stripe-js";
import { loadStripe, type StripeElementsOptions } from "@stripe/stripe-js";
import { useContext, useEffect, useState } from "react";
import { useTypedFetcher } from "remix-typedjson";
import { BookingFormContext } from "~/components/booking/ContextProvider";
import { route } from "~/utils/route";
import { type loader as totalPriceLoader } from "~/routes/resources+/book+/total-price";
import type { action as confirmationIntentFetcher } from "~/routes/resources+/book+/create-confirmation-intent";
import { Button } from "~/components/ui/button";
import { Loader2Icon } from "lucide-react";

export const StripePayment = ({ url }: { url: URL }) => {
  const context = useContext(BookingFormContext);
  if (!context) {
    throw new Error("BookingFormContext is not provided");
  }

  const {
    selectedBarberId,
    selectedServiceIds,
    selectedDate,
    selectedTime,
    name,
    phone,
  } = context;

  const totalPriceFetcher = useTypedFetcher<typeof totalPriceLoader>();
  const totalPrice = totalPriceFetcher.data?.totalPrice;

  const stripe = loadStripe(
    // if there is staging in the url or the domain is localhost, use the test key:
    url.hostname.includes("staging") || url.hostname.includes("localhost")
      ? "pk_test_51NG9gFG66f3EIERaVXlf92RRy4Q6In1iIVslHnWBbnkfmrxHCcoExKI78BdbGycqeUxZkJV9YmWYlKfpSX1CNa8Z00eP1KRjtr"
      : "pk_live_51NG9gFG66f3EIERaDsOktlglmAdUUoCywTHa4Sqs4Gz5NkzjCiaqtrbl7UTrhODeKdk2ZXKfoolSNT0gEp2ARlJw00HdPxAraX"
  );

  useEffect(() => {
    const url = route("/resources/book/total-price");
    const searchParams = new URLSearchParams();
    searchParams.set("barberId", selectedBarberId);
    selectedServiceIds.forEach((serviceId) => {
      searchParams.append("serviceIds", serviceId);
    });

    totalPriceFetcher.load(url + "?" + searchParams.toString());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBarberId, selectedServiceIds]);

  if (
    !stripe ||
    !totalPrice?.greaterThan(0) ||
    !selectedDate ||
    !selectedTime ||
    !name ||
    !phone
  ) {
    return (
      <div className="px-6">
        <Button id="submit" className={"mt-6 w-full py-2"} disabled>
          Complete missing fields
        </Button>
      </div>
    );
  }

  const options: StripeElementsOptions = {
    mode: "payment",
    currency: "gbp",
    amount: Math.round(parseFloat(totalPrice.toFixed(2)) * 100),
    appearance: {
      theme: "night",
    },
  };

  return (
    <div className="px-6">
      <Elements stripe={stripe} options={options}>
        <CheckoutForm />
      </Elements>
    </div>
  );
};

function CheckoutForm() {
  const stripe = useStripe();
  const elements = useElements();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const context = useContext(BookingFormContext);
  if (!context) {
    throw new Error("BookingFormContext is not provided");
  }

  const {
    selectedBarberId,
    selectedServiceIds,
    selectedDate,
    selectedTime,
    name,
    phone,
  } = context;

  const createConfirmationIntentFetcher =
    useFetcher<typeof confirmationIntentFetcher>();

  useEffect(() => {
    if (createConfirmationIntentFetcher.data?.err) {
      setErrorMessage(createConfirmationIntentFetcher.data.err.message);
      setIsLoading(false);
    }
  }, [createConfirmationIntentFetcher.data]);

  const onConfirm = async () => {
    if (!stripe || !elements) {
      // Stripe.js hasn't loaded yet.
      // Make sure to disable form submission until Stripe.js has loaded.
      return;
    }

    setIsLoading(true);

    if (!phone.match(/^(?:\+?44|0)?(?:0)?(7\d{9})$/)) {
      setErrorMessage(
        "Invalid phone number, only UK mobile numbers are allowed"
      );
      setIsLoading(false);
      return;
    }

    const { error: submitError } = await elements.submit();
    if (submitError) {
      setErrorMessage(submitError.message || "An error occurred");

      setIsLoading(false);
      return;
    }

    // Confirm the PaymentIntent using the details collected by the Express Checkout Element
    const { error, confirmationToken } = await stripe.createConfirmationToken({
      // `elements` instance used to create the Express Checkout Element
      elements,
    });

    if (error) {
      // This point is only reached if there's an immediate error when
      // confirming the payment. Show the error to your customer (for example, payment details incomplete)
      setErrorMessage(error.message || "An error occurred");

      setIsLoading(false);
      return;
    }

    createConfirmationIntentFetcher.submit(
      {
        barberId: selectedBarberId,
        serviceIds: selectedServiceIds,
        confirmationToken: confirmationToken.id,
        date: selectedDate,
        time: selectedTime,
        name,
        phone,
      },
      {
        action: route("/resources/book/create-confirmation-intent"),
        encType: "application/json",
        method: "POST",
        navigate: true,
      }
    );
  };

  return (
    <form
      id="payment-form"
      onSubmit={(e) => {
        e.preventDefault();
        onConfirm();
      }}
    >
      <ExpressCheckoutElement onConfirm={onConfirm} />
      <PaymentElement id="payment-element" className="mt-6" />
      <Button
        id="submit"
        className={"mt-6 w-full py-2"}
        disabled={
          isLoading ||
          !stripe ||
          !elements ||
          createConfirmationIntentFetcher.state !== "idle"
        }
      >
        {isLoading || createConfirmationIntentFetcher.state !== "idle" ? (
          <Loader2Icon className="mr-2 h-4 w-4 animate-spin" />
        ) : null}{" "}
        Pay Now
      </Button>
      {errorMessage && <div>{errorMessage}</div>}
    </form>
  );
}
